// Bluetooth device base: device request, subscription and automatic reconnection after a drop.
// Web Bluetooth types reduced to what we use (the API is not in TypeScript's DOM types).
import { t } from '../i18n/index.js';

export interface GattCharacteristic extends EventTarget {
  readonly value?: DataView;
  startNotifications(): Promise<GattCharacteristic>;
  readValue(): Promise<DataView>;
  writeValueWithResponse(v: BufferSource): Promise<void>;
}
export interface GattService { getCharacteristic(uuid: number | string): Promise<GattCharacteristic> }
export interface GattServer { readonly connected: boolean; connect(): Promise<GattServer>; disconnect(): void; getPrimaryService(uuid: number | string): Promise<GattService> }
export interface BtDevice extends EventTarget { readonly id: string; readonly name?: string; readonly gatt?: GattServer; watchAdvertisements?(o?: { signal?: AbortSignal }): Promise<void> }
type Filter = { services?: (number | string)[]; namePrefix?: string };
interface Bluetooth { requestDevice(o: { filters: Filter[]; optionalServices?: (number | string)[] }): Promise<BtDevice>; getDevices?(): Promise<BtDevice[]> }

export type StatusText = string | (() => string);
export type StatusFn = (s: 'on' | 'off' | 'wait', text: StatusText) => void;

export const bluetooth = (): Bluetooth | undefined => (navigator as Navigator & { bluetooth?: Bluetooth }).bluetooth;
export const needBluetooth = (): Bluetooth => {
  const b = bluetooth();
  if (!b) throw new Error(t.devices.noBluetooth);
  return b;
};

/** Value of a GATT notification. */
export const valueOf = (e: Event): DataView => (e.target as GattCharacteristic).value as DataView;

/** Delays (s) between reconnection attempts: about 30 s in total, enough for a sensor to wake up. */
const RETRY = [1, 2, 4, 8, 15];

export abstract class BleDevice {
  dev: BtDevice | null = null;
  private wanted = false;
  private tries = 0;
  private timer: ReturnType<typeof setTimeout> | null = null;
  constructor(protected readonly onStatus: StatusFn) {}

  get connected() { return !!this.dev?.gatt?.connected; }
  get name() { return this.dev?.name || this.fallbackName(); }

  protected abstract filters(): { filters: Filter[]; optionalServices?: (number | string)[] };
  /** Subscriptions after (re)connection. */
  protected abstract attach(server: GattServer): Promise<void>;
  /** Forgets connection-related state (counters, control point) after a drop. */
  protected detach(): void {}
  protected abstract fallbackName(): string;
  /** Status text once connected. */
  protected label(): string { return this.name; }

  async connect() {
    const dev = await needBluetooth().requestDevice(this.filters());
    await this.adopt(dev);
    return dev;
  }

  /** Connects a device already authorised by the browser (picked in a chooser, or remembered). */
  async adopt(dev: BtDevice) {
    // "Reconnect" starts from scratch, even on the same device: otherwise subscriptions would be doubled
    if (this.dev) this.teardown();
    dev.addEventListener('gattserverdisconnected', this.onDrop);
    this.dev = dev; this.wanted = true; this.tries = 0;
    // another device asked for meanwhile owns the slot: this attempt must not tear it down
    try { await this.open(); } catch (e) { if (this.dev === dev) this.teardown(); throw e; }
  }

  /** Intentional disconnect: no automatic reconnection. */
  disconnect() { this.teardown(); this.onStatus('off', () => t.devices.disconnected); }

  private teardown() {
    this.wanted = false;
    if (this.timer) clearTimeout(this.timer);
    this.timer = null;
    this.dev?.removeEventListener('gattserverdisconnected', this.onDrop);
    this.dev?.gatt?.disconnect();
    this.detach();
  }

  private async open() {
    const dev = this.dev, gatt = dev?.gatt;
    if (!gatt) throw new Error(t.devices.disconnected);
    this.onStatus('wait', () => t.devices.connecting);
    const server = await gatt.connect();
    // a connection that answers after the slot moved to another device must not subscribe to the old one
    if (this.dev !== dev) { gatt.disconnect(); throw new Error(t.devices.disconnected); }
    // failed subscription: disconnect to start from a clean state (the disconnect triggers reconnection if it is wanted)
    try { await this.attach(server); } catch (e) { gatt.disconnect(); throw e; }
    this.tries = 0;
    this.onStatus('on', () => this.label());
  }

  private onDrop = () => {
    this.detach();
    if (!this.wanted) return;
    this.retry();
  };

  private retry() {
    if (this.timer) return; // the drop and the failed attempt may both ask for a retry
    if (this.tries >= RETRY.length) { this.wanted = false; this.onStatus('off', () => t.devices.nameDisconnected(this.name)); return; }
    const n = ++this.tries;
    this.onStatus('wait', () => t.devices.reconnecting(n, RETRY.length));
    this.timer = setTimeout(() => {
      this.timer = null;
      if (!this.wanted || this.connected) return;
      this.open().catch(e => { console.warn('reconnect', this.name, e); this.detach(); if (this.wanted) this.retry(); });
    }, RETRY[n - 1] * 1000);
  }
}
